"use client";
import { recordsLimit } from "@/constant/constants";
import { formatNumber } from "@/helper/HelperFunction";
import { formatDate } from "helper/HelperFunction";
import { cloneElement, isValidElement } from "react";
import NoData from "../NoData/NoData";
import PaginationComponent from "../PaginationComponent";
import classes from "./TableStructure.module.css";
import { TiArrowDown, TiArrowUp } from "react-icons/ti";

const TableStructure = ({
  isLoading,
  scrollRef,
  setSort,
  sort,
  headerTitle = "",
  tableType = "default",
  isColumnColor = false,
  headerHandlers = {},
  tableContent = Array(5)
    .fill("")
    .map((_, index) => ({
      name: <div>Lorem</div>,
      contact: "+123456789",
      courseName: "lorem",
      courseType: "lorem",
      status: "lorem",
      time: formatDate(new Date()),
    })),

  tableHeaders = [
    { label: "NAME", value: "name", width: "50%" },
    { label: "CONTACT", value: "contact" },
    { label: "COURSE NAME", value: "courseName" },
    { label: "COURSE TYPE", value: "courseType" },
    { label: "STATUS", value: "status" },
    { label: "TIME", value: "time" },
  ],
  totalRecord = 20,
  recordLimit = recordsLimit,
  noDataMessage = "No Users Found",
  customStyle: tableCustomStyle,
  tableMinWidth,
  page = 1,
  setPage,
  tableFooter,
  // "compact" tightens the cells and shrinks the type so more rows and columns
  // fit on screen without zooming the browser out (the client was doing that
  // on every screen). Every table uses it; pass "default" for roomier spacing.
  density = "compact",
  // Opt-in, used only by the public family report, which parents read upright
  // on a phone. Lets the table scroll sideways inside its own box instead of
  // dragging the whole page with it. Every other screen is landscape-only and
  // wide enough, so the default is unchanged.
  fluid = false,
}) => {
  return (
    <>
      <style>{`
        .table-header-wrapper:has(.DropdownOptionContainer__control--menu-is-open){
          margin-bottom: -250px;
          padding-bottom: 250px;
        }
      `}</style>
      <div
        className={[
          classes.main,
          density === "compact" ? classes.compact : "",
          fluid ? classes.fluid : "",
        ].join(" ")}
      >
        <div className={headerTitle ? classes.__header : ""}>
          <div
            className={[
              classes.tableHeaderWrapper,
              "table-header-wrapper",
            ].join(" ")}
          >
            {headerTitle && (
              <span>
                {headerTitle && isValidElement(headerTitle)
                  ? cloneElement(headerTitle, {
                      style: { marginInlineEnd: "auto" },
                    })
                  : headerTitle}
              </span>
            )}

            {Object?.keys(headerHandlers)?.length > 0 && (
              <div className={classes.headerFilterDiv}>
                {Object?.keys(headerHandlers)?.map((e, index) => {
                  return <div key={e}>{headerHandlers[e]}</div>;
                })}
              </div>
            )}
          </div>
        </div>
        <div className={classes.scrollWrapper}>
          <div
            ref={scrollRef}
            style={tableCustomStyle}
            className={classes.tableWrapper}
          >
            <table
              className={classes.table}
              // A fluid table keeps its minimum only while there is room for
              // it; below that it is the scroll box, not the page, that moves.
              style={{
                minWidth: fluid
                  ? `min(${tableMinWidth}px, 100%)`
                  : `${tableMinWidth}px`,
              }}
            >
              <thead className={classes.tableHeader}>
                <tr
                  className={classes.tableRow}
                  data-type="header"
                  style={{
                    "--border-bottom": "0px solid var(--border-color)",
                  }}
                >
                  {Array.isArray(tableHeaders) &&
                    tableHeaders?.map((e, index) => {
                      if (!e) {
                        return;
                      }
                      return (
                        <th
                          className={classes.tableHead}
                          style={{
                            width: e?.width,
                            ...e?.headerStyle,
                            ...(tableType == "secondary"
                              ? {
                                  borderBottom:
                                    "1px solid var(--primary-text-color)",
                                }
                              : {}),
                          }}
                          key={index}
                          {...(e?.legend && e?.legend
                            ? { title: e?.legend }
                            : {})}
                        >
                          {e?.label}
                          {e?.isSortable &&
                            setSort &&
                            (sort?.sortOrder === "desc" &&
                            sort?.sortBy === e?.value ? (
                              <span
                                className={classes.sortIcon}
                                data-variant={
                                  sort?.sortBy === e?.value
                                    ? "active"
                                    : "inactive"
                                }
                                onClick={() =>
                                  setSort({
                                    sortBy: e?.value,
                                    sortOrder: "asc",
                                  })
                                }
                              >
                                <TiArrowDown />
                              </span>
                            ) : (
                              <span
                                className={classes.sortIcon}
                                data-variant={
                                  sort?.sortBy === e?.value
                                    ? "active"
                                    : "inactive"
                                }
                                onClick={() =>
                                  setSort({
                                    sortBy: e?.value,
                                    sortOrder: "desc",
                                  })
                                }
                              >
                                <TiArrowUp />
                              </span>
                            ))}
                        </th>
                      );
                    })}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <>
                    {Array(recordLimit)
                      .fill(0)
                      .map((item, index) => (
                        <tr className={classes.tableRow}>
                          {tableHeaders?.map((item) => (
                            <td
                              className={classes.tableData}
                              style={{
                                paddingInline: "10px",
                                paddingBlock: "10px 0px",
                              }}
                            >
                              <div
                                className="animate-pulse"
                                style={{
                                  height:
                                    density === "compact" ? "28px" : "40px",
                                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                                  borderRadius: "var(--global-border-radius)",
                                  width: "100%",
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                  </>
                ) : tableContent.length > 0 ? (
                  tableContent?.map((item, index) => {
                    return (
                      <tr
                        data-style={tableType}
                        key={index}
                        className={classes.tableRow}
                        style={{
                          "--border-bottom": "1px solid var(--border-color)",
                          "--bg": item?.rowColor
                            ? item?.rowColor
                            : "var(--background)",
                          "--text": item?.textColor
                            ? item?.textColor
                            : "var(--text-color)",
                        }}
                      >
                        {tableHeaders?.map((e, index) => {
                          if (!e) {
                            return;
                          }
                          const customProps =
                            e?.formatting?.[e.value] &&
                            !isValidElement(item?.[e?.value])
                              ? e?.formatting[e.value](item)
                              : {};
                          const customClasses = customProps.className
                            ? structuredClone(customProps.className)
                            : undefined;
                          const customStyles = customProps.style
                            ? structuredClone(customProps.style)
                            : {};

                          delete customProps["style"];
                          delete customProps["className"];
                          return (
                            <td
                              className={[
                                classes.tableData,
                                customClasses,
                              ].join(" ")}
                              style={{
                                width: e?.width,
                                ...e?.dataStyle,
                                ...customStyles,
                                ...(tableType === "secondary"
                                  ? {
                                      borderBottom:
                                        "1px solid var(--gray-border)",
                                    }
                                  : {}),
                                ...(isColumnColor
                                  ? {
                                      backgroundColor:
                                        item?.backgroundColor?.[index] ||
                                        "transparent",
                                      // left undefined unless the row supplies
                                      // one, so cells keep inheriting the
                                      // default text colour as before
                                      color: item?.textColor?.[index],
                                    }
                                  : {}),
                              }}
                              key={index}
                              {...customProps}
                            >
                              {e?.type == "amount"
                                ? formatNumber(item?.[e?.value])
                                : item?.[e?.value]}{" "}
                              {e?.symbol === "%" && e?.symbol}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={tableHeaders?.length + 1}>
                      <NoData text={noDataMessage} />
                    </td>
                  </tr>
                )}
              </tbody>
              {tableFooter && (
                <tfoot className={classes.tableFooter}>
                  <tr
                    className={classes.tableRow}
                    style={{ "background-color": "var(--tertiary-color)" }}
                  >
                    {Array.isArray(tableFooter) ? (
                      tableFooter?.map((e, index) => {
                        return (
                          <th
                            className={classes.tableHead}
                            style={{
                              ...e?.headerStyle,
                              ...tableHeaders[index]?.footerStyle,
                              ...(tableType === "secondary"
                                ? {
                                    borderBottom:
                                      "1px solid var( --primary-text-color)",
                                  }
                                : {}),
                            }}
                            key={index}
                          >
                            {e}
                          </th>
                        );
                      })
                    ) : (
                      <>
                        {Array(tableFooter.emptyColumns)
                          .fill("")
                          .map((e) => (
                            <td></td>
                          ))}
                        <td>{tableFooter.content}</td>
                      </>
                    )}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
        {tableContent?.length > 0 && page && totalRecord > 0 && (
          <div className={classes.Pagination}>
            <PaginationComponent
              totalPages={Math.ceil(totalRecord / recordLimit)}
              setCurrentPage={setPage}
              currentPage={Number(page)}
              totalRecord={totalRecord}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default TableStructure;
